import type { PortalDTO } from "@mm-sales/shared";

/**
 * Everything the portal renders comes from the imported shop (Uber Eats /
 * DoorDash / Grubhub / Google / their website) merged with what the agent
 * typed on the prospect. This is the ONE place that decides precedence and
 * fallbacks, so every section shows the same name, logo, hero, menu and hours.
 */
export type Brand = {
  name: string;
  cuisine: string;
  city: string;
  state: string;
  location: string;
  tagline: string;
  headline: [string, string];
  /** Line under the website headline — never the same words as the headline. */
  heroSub: string;
  description: string;
  logo?: string;
  hero?: string;
  images: string[];
  menu: { name: string; items: NonNullable<PortalDTO["shop"]>["items"] }[];
  items: NonNullable<PortalDTO["shop"]>["items"];
  hours: NonNullable<PortalDTO["shop"]>["hours"];
  address: string;
  phone: string;
  website: string;
  websiteHost: string;
  rating?: number;
  reviewCount?: number;
  color: string;
  /** Full brand system derived from the prospect's colours + fonts (defaults when unknown). */
  palette: Palette;
  /** Fallback "logo": first letters of the name, drawn on the brand colour. */
  monogram: string;
  first: string;
  initials: string;
  locationsCount: number;
  /** Multi-location demos: every provisioned location. Single-location demos: empty. */
  locations: PortalDTO["locations"];
  agent: PortalDTO["agent"];
};

const DAY = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export const dayName = (d: number) => DAY[d] ?? "";
export const fmtTime = (hhmm: string) => {
  const [h = 0, m = 0] = hhmm.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  return `${((h + 11) % 12) + 1}${m ? `:${String(m).padStart(2, "0")}` : ""} ${ap}`;
};
export const money = (n: number) => `$${n.toFixed(2)}`;
/** Older imports stored HTML entities in names ("Mac &amp; Cheese"); never show them raw. */
export const clean = (s: string) =>
  s
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ");

export function brandOf(d: PortalDTO): Brand {
  const s = d.shop;
  const b = s?.business;
  const name = d.prospect.businessName || b?.name || "Your Restaurant";
  const cuisine = d.prospect.cuisine || b?.cuisine || "";
  const city = d.prospect.city || b?.address.city || "";
  const state = d.prospect.state || b?.address.state || "";
  const description =
    b?.description ||
    `${name} brings ${cuisine ? `the heart of ${cuisine} cooking` : "great food"} to ${city || "the neighborhood"}. Fresh ingredients, family recipes, and every dish crafted with care.`;
  const tagline =
    b?.tagline ||
    (cuisine
      ? `Authentic ${cuisine} cuisine. Made with love.`
      : "Made with love, served with care.");
  // Website hero headline: two lines. The site's own tagline when short, else a warm default.
  const headline: [string, string] =
    b?.tagline && b.tagline.length <= 34 && b.tagline.includes(" ")
      ? splitHalf(b.tagline)
      : ["Good Food,", "Brighter Days"];
  const cuisineLine = cuisine
    ? `Authentic ${cuisine} cuisine. Made with love.`
    : "Made with love, served with care.";
  const heroSub = headline.join(" ") === tagline ? cuisineLine : tagline;
  const hero =
    d.prospect.heroImageUrl ||
    b?.heroImageUrl ||
    s?.images[0] ||
    s?.items.find((i) => i.imageUrl)?.imageUrl ||
    undefined;
  const logo = d.prospect.logoUrl || b?.logoUrl || undefined;
  const imgs = new Set<string>();
  for (const u of s?.images ?? []) if (u !== hero) imgs.add(u);
  for (const it of s?.items ?? []) if (it.imageUrl && it.imageUrl !== hero) imgs.add(it.imageUrl);
  const items = (s?.items ?? []).map((i) => ({
    ...i,
    name: clean(i.name),
    description: clean(i.description),
    category: clean(i.category),
  }));
  const catOrder = (s?.categories ?? [])
    .slice()
    .sort((a, z) => a.sortOrder - z.sortOrder)
    .map((c) => c.name);
  const names = [...new Set([...catOrder, ...items.map((i) => i.category)])];
  const menu = names
    .map((n) => ({ name: n, items: items.filter((i) => i.category === n) }))
    .filter((g) => g.items.length);
  const website = d.prospect.website || b?.website || "";
  let websiteHost = "";
  try {
    websiteHost = website
      ? new URL(website.startsWith("http") ? website : `https://${website}`).host
      : "";
  } catch {
    websiteHost = website;
  }
  const palette = paletteOf({
    name,
    cuisine,
    primary: d.prospect.brandColor,
    secondary: d.prospect.brandSecondaryColor,
    headingFont: d.prospect.brandHeadingFont,
    bodyFont: d.prospect.brandBodyFont,
  });
  const monogram = name
    .replace(/[^A-Za-z0-9 ]/g, " ")
    .split(/\s+/)
    .filter((w) => w && !/^(the|and|of|a|an|&)$/i.test(w))
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
  const first = d.prospect.contact.firstName || "";
  const initials =
    `${first[0] ?? ""}${d.prospect.contact.lastName[0] ?? ""}`.toUpperCase() ||
    name.slice(0, 2).toUpperCase();
  return {
    name,
    cuisine,
    city,
    state,
    location: [city, state].filter(Boolean).join(", "),
    tagline,
    headline,
    heroSub,
    description,
    logo,
    hero,
    images: [...imgs].slice(0, 12),
    menu,
    items,
    hours: s?.hours ?? [],
    address: d.prospect.address,
    phone: d.prospect.phone || d.prospect.contact.phone,
    website,
    websiteHost: websiteHost || `www.${name.toLowerCase().replace(/[^a-z0-9]+/g, "")}.com`,
    rating: b?.rating ?? undefined,
    reviewCount: b?.reviewCount ?? undefined,
    color: palette.primary,
    palette,
    monogram,
    first,
    initials,
    locationsCount: Math.max(d.prospect.locationsCount, d.locations.length),
    locations: d.locations,
    agent: d.agent,
  };
}

/* ------------------------------------------------------------------ brand system */

export type Palette = {
  primary: string;
  /** Hover / pressed shade of primary. */
  primaryDark: string;
  /** Tinted background (chips, selected rows) that reads on white. */
  primarySoft: string;
  /** Text colour that stays legible on primary (white on dark brands, near-black on light ones). */
  onPrimary: string;
  secondary: string;
  onSecondary: string;
  headingFont: string;
  bodyFont: string;
  /** Google Fonts families to load for this brand (only the non-default ones). */
  fontsToLoad: string[];
};

/**
 * Curated pairs so a prospect with no detectable colours still gets a distinct,
 * restaurant-appropriate identity rather than the same default for everyone.
 * Picked by a hash of the name, so the same prospect always gets the same pair.
 */
const DEFAULT_PAIRS: [string, string][] = [
  ["#c8102e", "#1f2937"],
  ["#b45309", "#111827"],
  ["#0f766e", "#f59e0b"],
  ["#7c2d12", "#fbbf24"],
  ["#1e3a8a", "#f97316"],
  ["#166534", "#fde68a"],
  ["#9d174d", "#0f172a"],
  ["#4338ca", "#f472b6"],
  ["#a16207", "#1c1917"],
  ["#0e7490", "#f43f5e"],
];
const DEFAULT_HEADING = "Playfair Display";
const DEFAULT_BODY = "Inter";
/** Families index.html already loads; anything else gets a Google Fonts link at runtime. */
const PRELOADED_FONTS = new Set([DEFAULT_HEADING, DEFAULT_BODY]);

const hashOf = (s: string) => {
  let h = 2166136261;
  for (const ch of s.toLowerCase()) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  return Math.abs(h >>> 0);
};

const HEX = /^#[0-9a-fA-F]{6}$/;
const rgbOf = (hex: string): [number, number, number] => {
  const n = Number.parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const hexOf = (r: number, g: number, b: number) =>
  `#${[r, g, b]
    .map((v) =>
      Math.max(0, Math.min(255, Math.round(v)))
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;
/** Relative luminance (WCAG) — decides white vs dark text on a colour. */
const luminance = (hex: string) => {
  const [r, g, b] = rgbOf(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
export const onColor = (hex: string) => (luminance(hex) > 0.4 ? "#111318" : "#ffffff");
/** Mix a colour towards black (amt<0) or white (amt>0). */
export const mix = (hex: string, amt: number) => {
  const [r, g, b] = rgbOf(hex);
  const t = amt > 0 ? 255 : 0;
  const k = Math.abs(amt);
  return hexOf(r + (t - r) * k, g + (t - g) * k, b + (t - b) * k);
};

export function paletteOf(input: {
  name: string;
  cuisine?: string;
  primary?: string | null;
  secondary?: string | null;
  headingFont?: string | null;
  bodyFont?: string | null;
}): Palette {
  const pair = DEFAULT_PAIRS[hashOf(input.name || "default") % DEFAULT_PAIRS.length]!;
  const primary = input.primary && HEX.test(input.primary) ? input.primary.toLowerCase() : pair[0];
  // A detected secondary too close to primary is useless as a contrast; fall back to a dark tone.
  const secondary =
    input.secondary && HEX.test(input.secondary) && input.secondary.toLowerCase() !== primary
      ? input.secondary.toLowerCase()
      : input.primary
        ? mix(primary, -0.55)
        : pair[1];
  const headingFont = (input.headingFont || "").trim() || DEFAULT_HEADING;
  const bodyFont = (input.bodyFont || "").trim() || DEFAULT_BODY;
  return {
    primary,
    primaryDark: mix(primary, -0.18),
    primarySoft: mix(primary, 0.88),
    onPrimary: onColor(primary),
    secondary,
    onSecondary: onColor(secondary),
    headingFont,
    bodyFont,
    fontsToLoad: [...new Set([headingFont, bodyFont])].filter((f) => !PRELOADED_FONTS.has(f)),
  };
}

/** CSS variables every mock reads (`var(--brand)` etc.); set once on the portal root. */
export const brandVars = (b: Pick<Brand, "palette">): Record<string, string> => ({
  "--brand": b.palette.primary,
  "--brand-dark": b.palette.primaryDark,
  "--brand-soft": b.palette.primarySoft,
  "--on-brand": b.palette.onPrimary,
  "--brand-2": b.palette.secondary,
  "--on-brand-2": b.palette.onSecondary,
  "--font-heading": `'${b.palette.headingFont}', 'Playfair Display', Georgia, serif`,
  "--font-body": `'${b.palette.bodyFont}', Inter, system-ui, sans-serif`,
});

function splitHalf(s: string): [string, string] {
  const words = s.split(" ");
  const mid = Math.ceil(words.length / 2);
  return [words.slice(0, mid).join(" "), words.slice(mid).join(" ")];
}

/** Sample dishes so a demo built before the import still shows a believable menu. */
export const sampleMenu = (cuisine: string): Brand["menu"] => {
  const c = cuisine.toLowerCase();
  const pick = c.includes("mex")
    ? [
        ["Tacos", ["Al Pastor Tacos", 12], ["Carnitas Tacos", 12], ["Veggie Tacos", 11]],
        ["Mains", ["Chicken Enchiladas", 16], ["Carne Asada", 22]],
        ["Desserts", ["Churros", 7], ["Flan", 6]],
      ]
    : c.includes("pizza") || c.includes("ital")
      ? [
          [
            "Pasta",
            ["Margherita Pizza", 14],
            ["Pasta Alfredo", 16],
            ["Caesar Salad", 12],
            ["Grilled Salmon", 22],
          ],
          ["Desserts", ["Tiramisu", 8], ["Garlic Bread", 6]],
        ]
      : c.includes("asia") || c.includes("sushi") || c.includes("thai")
        ? [
            ["Starters", ["Spring Rolls", 8], ["Edamame", 6]],
            ["Mains", ["Pad Thai", 15], ["Chicken Teriyaki", 16], ["Salmon Roll", 14]],
            ["Desserts", ["Mochi", 6]],
          ]
        : [
            ["Starters", ["House Salad", 9], ["Soup of the Day", 7]],
            ["Mains", ["Grilled Chicken", 18], ["Steak Frites", 26], ["Fish & Chips", 17]],
            ["Desserts", ["Cheesecake", 8]],
          ];
  return pick.map(([name, ...rows]) => ({
    name: name as string,
    items: (rows as [string, number][]).map(([n, p]) => ({
      name: n,
      description: "",
      price: p,
      category: name as string,
      modifiers: [],
      variants: [],
      available: true,
      imageUrl: undefined,
      externalId: undefined,
    })),
  }));
};
